import { html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { parse } from "yaml";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import { download, getPackage, listResources, type ResourceRecord } from "../api/registry.js";
import { humanError } from "../api/errors.js";
import { loadEditor } from "../lib/load-editor.js";
import { extractCompat, type CompatLabels } from "../lib/compat-labels.js";
import { previewType, type PreviewType } from "../lib/preview.js";
import "../components/copy-pull.js";
import "../components/snapshot-preview.js";
import "../components/resources-panel.js";

/**
 * page-version shows the exact YAML bytes of a chosen version — the bytes the CLI
 * pulls — fetched from GET /api/registry/download/{scope}/{name}/{version}, with the
 * version's digest (from the ETag) displayed (R11), plus the stored preview snapshot
 * of THAT exact version (schematic fallback) and the compat ranges declared in the
 * document's labels. The YAML is shown in a read-only CodeMirror editor (lazily
 * loaded chunk); until that chunk arrives it renders as escaped text in a <pre>
 * (lit escapes interpolated text — no HTML injection, N7).
 */
@customElement("page-version")
export class PageVersion extends PageElement {
  static override styles = [
    shared,
    css`
      h1 {
        overflow-wrap: anywhere;
      }
      .digest {
        font-family: var(--mono);
        font-size: 13px;
        word-break: break-all;
      }
      .compat {
        margin-bottom: var(--sp-3);
      }
      snapshot-preview {
        height: 280px;
      }
      pre,
      yaml-editor {
        max-height: 70vh;
      }
    `,
  ];

  @property() scope = "";
  @property() name = "";
  @property() version = "";

  @state() private yaml = "";
  @state() private digest = "";
  @state() private errorMsg = "";
  @state() private loading = false;
  @state() private editorReady = false;
  @state() private compat: CompatLabels | null = null;
  @state() private fallbackType: PreviewType = "column";
  @state() private resources: ResourceRecord[] = [];

  override connectedCallback() {
    super.connectedCallback();
    void loadEditor().then(() => (this.editorReady = true));
  }

  override willUpdate(changed: PropertyValues) {
    if (changed.has("scope") || changed.has("name") || changed.has("version")) {
      void this.load();
    }
  }

  private async load() {
    if (!this.scope || !this.name || !this.version) return;
    this.setTitle(`@${this.scope}/${this.name}@${this.version}`);
    this.loading = true;
    this.errorMsg = "";
    // Best-effort: kind/category only sharpen the schematic fallback.
    void getPackage(this.scope, this.name)
      .then((p) => (this.fallbackType = previewType(p.kind, p.category)))
      .catch(() => {});
    try {
      const { yaml, digest } = await download(this.scope, this.name, this.version);
      this.yaml = yaml;
      this.digest = digest;
      this.compat = parseCompat(yaml);
    } catch (e) {
      this.errorMsg = humanError(e);
      this.yaml = "";
      this.compat = null;
    } finally {
      this.loading = false;
    }
    this.resources = await listResources(this.scope, this.name, this.version).catch(() => []);
  }

  override render() {
    const coord = `@${this.scope}/${this.name}`;
    return html`
      <div class="wrap-narrow">
        <h1>${coord}<span class="mono"> @ ${this.version}</span></h1>
        <p>
          <a href=${`/p/${this.scope}/${this.name}`}>← back to package</a>
        </p>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.compat && (this.compat.engine || this.compat.cli)
          ? html`<div class="compat">
              ${this.compat.engine
                ? html`<span class="chip mono">engine ${this.compat.engine}</span>`
                : ""}
              ${this.compat.cli ? html`<span class="chip mono">cli ${this.compat.cli}</span>` : ""}
            </div>`
          : ""}
        ${this.errorMsg
          ? ""
          : html`<div class="card">
              <h2>Preview</h2>
              <snapshot-preview
                .scope=${this.scope}
                .name=${this.name}
                .version=${this.version}
                .fallbackType=${this.fallbackType}
              ></snapshot-preview>
            </div>`}
        ${this.digest
          ? html`<div class="card">
              <div class="muted">digest</div>
              <div class="digest">${this.digest}</div>
            </div>`
          : ""}
        ${this.errorMsg
          ? ""
          : html`<div class="card">
              <h2>Resources</h2>
              <resources-panel
                .resources=${this.resources}
                .scope=${this.scope}
                .name=${this.name}
                .version=${this.version}
              ></resources-panel>
            </div>`}
        ${this.errorMsg
          ? ""
          : html`<div class="card">
              <h2>Document (${this.version})</h2>
              ${this.loading
                ? html`<p class="muted">Loading…</p>`
                : this.editorReady
                  ? html`<yaml-editor .value=${this.yaml} readOnly></yaml-editor>`
                  : html`<pre>${this.yaml}</pre>`}
            </div>`}
      </div>
    `;
  }
}

/** parseCompat reads the compat labels out of the frozen YAML bytes, best-effort. */
function parseCompat(yaml: string): CompatLabels {
  try {
    const labels =
      (parse(yaml) as { metadata?: { labels?: Record<string, string> } } | null)?.metadata
        ?.labels ?? {};
    return extractCompat(labels as Record<string, string>);
  } catch {
    return extractCompat({});
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-version": PageVersion;
  }
}
